import React, { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
export interface ButtonProps extends
  React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  variant?: 'primary' | 'secondary' | 'outline' | 'darkOutline' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  isLoading?: boolean;
}
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
  {
    className,
    variant = 'primary',
    size = 'default',
    asChild = false,
    isLoading,
    children,
    ...props
  },
  ref) =>
  {
    const Comp = asChild ? Slot : 'button';
    const baseStyles =
    'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-semibold transition duration-200 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[#0a6248] focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:pointer-events-none disabled:opacity-100 hover:-translate-y-0.5 active:translate-y-0';
    const variants = {
      primary:
      'bg-verto-teal text-white hover:bg-verto-deep disabled:bg-[#667a70] disabled:text-white disabled:shadow-none shadow-[0_12px_28px_rgba(184,61,39,0.20)]',
      secondary:
      'bg-verto-black text-verto-white hover:bg-verto-stone disabled:bg-[#667a70] disabled:text-white disabled:shadow-none shadow-[0_12px_28px_rgba(23,23,19,0.16)]',
      outline:
      'border border-verto-black/25 bg-transparent text-verto-black hover:border-verto-black hover:bg-verto-black hover:text-verto-white disabled:border-[#71847b] disabled:text-[#425e52]',
      darkOutline:
      'border border-white/25 bg-transparent text-verto-white hover:border-verto-white hover:bg-verto-white hover:text-verto-black disabled:border-[#82978d] disabled:text-[#dce9e2]',
      ghost: 'text-verto-black hover:bg-verto-black/5 disabled:text-[#425e52]',
      link: 'text-verto-deep underline-offset-4 hover:underline hover:translate-y-0 disabled:text-[#425e52]'
    };
    const sizes = {
      default: 'h-10 px-4 py-2',
      sm: 'h-9 px-3.5',
      lg: 'h-14 px-7 text-base',
      icon: 'h-10 w-10'
    };
    const variantClass = variants[variant];
    const sizeClass = sizes[size];
    return (
      <Comp
        className={`${baseStyles} ${variantClass} ${sizeClass} ${className || ''}`}
        ref={ref}
        aria-busy={isLoading || undefined}
        disabled={isLoading || props.disabled}
        {...props}>

        {asChild ? children : <>{isLoading && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}{children}</>}
      </Comp>);

  }
);
Button.displayName = 'Button';
export { Button };
